/**
 * Libellés CMS avancé réutilisés — mêmes clés partout (flows, modales, actions).
 */
import type { CmsNewKey } from "@/lib/cms/cms-new-i18n";

type Phrase = readonly [CmsNewKey, string];

export const CMS_NEW_PHRASES = {
  editTemplate: ["cms.new.flow.editTemplate", "Éditer le template"],
  createTemplate: ["cms.new.flow.createTemplate", "Créer le template"],
  creating: ["cms.new.flow.creating", "Création…"],
  homeDefined: ["cms.new.home.defined", "Définie"],
  homeToDefine: ["cms.new.home.toDefine", "À définir"],
  openInNewTab: ["cms.new.link.openInNewTab", "Ouvrir dans un nouvel onglet"],
  cancel: ["cms.new.common.cancel", "Annuler"],
  back: ["cms.new.common.back", "Retour"],
  delete: ["cms.new.common.delete", "Supprimer"],
  create: ["cms.new.common.create", "Créer"],
  draftPublishHint: [
    "cms.new.flow.draftPublishHint",
    "Cette page est en brouillon — publiez-la pour qu'elle s'affiche sur le site.",
  ],
  browseCatalog: ["cms.new.medias.browseCatalog", "Parcourir le catalogue"],
  addMedia: ["cms.new.medias.addMedia", "Ajouter un média"],
  addFirstMedia: ["cms.new.medias.addFirstMedia", "Ajouter le premier média"],
  backToSearch: ["cms.new.medias.backToSearch", "Retour à la recherche"],
  previewUnavailable: ["cms.new.medias.previewUnavailable", "Aperçu indisponible"],
  mediaPosition: ["cms.new.medias.position", "Position"],
  mediaId: ["cms.new.medias.mediaId", "Identifiant média"],
  saveDecoupages: ["cms.new.decoupages.save", "Enregistrer les découpages"],
  saving: ["cms.new.common.saving", "Enregistrement…"],
  dirtyUnsaved: ["cms.new.common.dirtyUnsaved", "Modifications non enregistrées"],
  dirtySaved: ["cms.new.common.dirtySaved", "Toutes les modifications sont enregistrées"],
  recentPages: ["cms.new.home.recentPages", "Dernières pages modifiées"],
  createFirstPage: ["cms.new.pages.createFirst", "Créer la première page"],
  published: ["cms.new.pages.published", "Publiée"],
  draft: ["cms.new.pages.draft", "Brouillon"],
  homeBadge: ["cms.new.home.badge", "Accueil"],
  unnamedPage: ["cms.new.pages.unnamed", "(sans nom)"],
  newPageTitle: ["cms.new.pages.newPageTitle", "Nouvelle page"],
  newPageDescription: [
    "cms.new.pages.newPageDescription",
    "Donne un nom à la page. Tu pourras ensuite éditer son contenu et son SEO.",
  ],
  deletePageDescription: [
    "cms.new.pages.deletePageDescription",
    "Cette action est définitive. La page sera supprimée avec ses URLs.",
  ],
  artworksTab: ["cms.new.decoupages.artworksTab", "Œuvres"],
  multiplesTab: ["cms.new.decoupages.multiplesTab", "Multiples"],
  mixedCatalogHint: [
    "cms.new.decoupages.mixedCatalogHint",
    "Catalogue fusionné (œuvres + multiples) — un seul jeu de découpages.",
  ],
  targetTypeAria: ["cms.new.decoupages.targetTypeAria", "Type de cible"],
  editPage: ["cms.new.flow.editPage", "Éditer la page"],
  mediasSubtitle: [
    "cms.new.medias.subtitle",
    "La médiathèque du site — les mêmes médias que ceux proposés par les blocs.",
  ],
  mediasAddDialogDescription: [
    "cms.new.medias.addDialogDescription",
    "Image, vidéo ou embed — ajouté à la médiathèque du site (visible par tous les blocs).",
  ],
  mediasDeleteDialogDescription: [
    "cms.new.medias.deleteDialogDescription",
    "Cette action est définitive. Le média sera supprimé de la médiathèque. La suppression est refusée s'il est utilisé par une page du site.",
  ],
  mediasNoSearchResults: [
    "cms.new.medias.noSearchResults",
    "Aucun média ne correspond à la recherche.",
  ],
  mediasEmpty: ["cms.new.medias.empty", "Aucun média pour le moment."],
  mediasCmsNotFound: [
    "cms.new.medias.cmsNotFound",
    "CMS introuvable pour cette instance.",
  ],
  mediasCatalogBrowseHint: [
    "cms.new.medias.catalogBrowseHint",
    "Cette image provient du catalogue. Pour l'utiliser sur le site, sélectionnez-la via l'onglet Catalogue d'un bloc média dans l'éditeur de page.",
  ],
  mediasDeleteFailed: ["cms.new.medias.deleteFailed", "Échec de la suppression"],
  mediasDeleteBlocked: ["cms.new.medias.deleteBlocked", "Suppression impossible"],
  abonnementSubtitle: [
    "cms.new.abonnement.subtitle",
    "Personnalisez la page /abonnement avec des blocs CMS avancé et des boutons de paiement en mode consultation.",
  ],
  decoupagesSubtitleMixed: [
    "cms.new.decoupages.subtitleMixed",
    "Catalogue raisonné (mode fusionné) — un seul jeu de découpages, appliqué aux œuvres et aux multiples.",
  ],
  decoupagesSubtitleDefault: [
    "cms.new.decoupages.subtitleDefault",
    "Découpages du catalogue raisonné — la grille de navigation par cible (Œuvres / Multiples).",
  ],
  maintenanceNoPage: [
    "cms.new.maintenance.noPageAssigned",
    "Aucune page assignée : la maintenance ne peut pas s'afficher.",
  ],
  maintenancePageLabel: ["cms.new.maintenance.pageLabel", "Page de maintenance"],
  abonnementPageLabel: ["cms.new.abonnement.pageLabel", "Page d'abonnement"],
  galleryChooseMedia: ["cms.new.gallery.chooseMedia", "Choisir des médias"],
  galleryEditMedia: ["cms.new.gallery.editMedia", "Modifier ({count})"],
  galleryEmptyLibrary: [
    "cms.new.gallery.emptyLibrary",
    "Aucun média dans la médiathèque.",
  ],
  galleryNoElement: [
    "cms.new.gallery.noElement",
    "Aucun élément disponible.",
  ],
  kindImage: ["cms.new.medias.kind.image", "Image"],
  kindVideo: ["cms.new.medias.kind.video", "Vidéo"],
  kindEmbed: ["cms.new.medias.kind.embed", "Embed"],
  searchResultsArtworks: ["cms.new.search.resultsArtworks", "Résultats œuvres"],
  searchResultsMultiples: ["cms.new.search.resultsMultiples", "Résultats multiples"],
  menuEditItem: ["cms.new.menus.editItem", "Modifier l'élément"],
  menuNewItem: ["cms.new.menus.newItem", "Nouvel élément de menu"],
  menuItemDescription: [
    "cms.new.menus.itemDescription",
    "Choisis une page du site ou un lien personnalisé, son libellé par langue et sa position.",
  ],
  menuLabelPlaceholderPage: [
    "cms.new.menus.labelPlaceholderPage",
    "Laisser vide = titre de la page",
  ],
  menuLabelPlaceholderCustom: ["cms.new.menus.labelPlaceholderCustom", "Ex. Boutique"],
  menuSubmenuParent: ["cms.new.menus.submenuParent", "Sous-menu de (optionnel)"],
  menuMaxNesting: ["cms.new.menus.maxNesting", "4 niveaux d'imbrication maximum."],
  linkInternalPageHint: [
    "cms.new.link.internalPageHint",
    "Le lien suit automatiquement la langue et le slug de la page publiée. Les templates catalogue (listing / fiche) sont exclus.",
  ],
  linkDecoupageHint: [
    "cms.new.link.decoupageHint",
    "Lien vers le découpage sélectionné (template listing). La page parente est celle où se trouve le bouton.",
  ],
  linkChooseDecoupage: [
    "cms.new.link.chooseDecoupage",
    "Choisissez un découpage pour activer le lien.",
  ],
  linkPreviewLangHint: [
    "cms.new.link.previewLangHint",
    "Langue par défaut du site. Le lien final suit la langue du visiteur sur le site public.",
  ],
  galleryClose: ["cms.new.gallery.close", "Fermer"],
  galleryDone: ["cms.new.gallery.done", "Terminé"],
  galleryMore: ["cms.new.gallery.more", "+{count} autres"],
  createTemplateFailed: [
    "cms.new.flow.createTemplateFailed",
    "Impossible de créer le template",
  ],
  shellLangSelectorDesc: [
    "cms.new.shell.langSelectorDesc",
    "langues actives du site",
  ],
  memberFlowIntro: [
    "cms.new.memberFlow.intro",
    "Sans liaison, le site affiche les pages classiques. Avec un template publié, la page CMS avancé s'affiche sur la route système correspondante (/connexion, /inscription, etc.). Vous pouvez enrichir le template avec d'autres blocs CMS avancé autour du bloc fonctionnel.",
  ],
  searchFlowIntro: [
    "cms.new.searchFlow.intro",
    "Les URLs de recherche restent inchangées (/[page]/search?query=…). Sans liaison, le site affiche les pages classiques.",
  ],
  catalogFlowIntro: [
    "cms.new.catalogFlow.intro",
    "Les URLs du catalogue restent inchangées (/[page]/[découpage] puis /[id]). Sans liaison, le site affiche les pages classiques.",
  ],
  newsEditTitle: ["cms.new.news.editTitle", "Modifier l'actualité"],
  newsAddTitle: ["cms.new.news.addTitle", "Ajouter une actualité"],
  newsAddButton: ["cms.new.news.addButton", "Ajouter une actualité"],
  newsNoTitle: ["cms.new.news.noTitle", "(Sans titre)"],
  newsImageColumn: ["cms.new.news.imageColumn", "Image"],
  newsDateColumn: ["cms.new.news.dateColumn", "Date"],
  newsSubtitle: [
    "cms.new.news.subtitle",
    "Les articles du site — publiés ou en brouillon. Seuls les articles publiés apparaissent sur le site.",
  ],
  pickerNoResults: ["cms.new.picker.noResults", "Aucun résultat."],
  pickerNoItems: ["cms.new.picker.noItems", "Aucun élément disponible."],
  mediaLibraryEmpty: [
    "cms.new.medias.libraryEmpty",
    "Aucun média dans la bibliothèque du CMS. Ajoutez-en via l'onglet « Ajouter un média ».",
  ],
  chooseMedia: ["cms.new.medias.chooseMedia", "Choisir un média"],
  changeMedia: ["cms.new.medias.changeMedia", "Changer"],
  mediaTabLibrary: ["cms.new.medias.tabLibrary", "Bibliothèque"],
  mediaTabCatalog: ["cms.new.medias.tabCatalog", "Catalogue"],
  buttonHoverSection: ["cms.new.button.hoverSection", "Au survol"],
  seoRootSlug: ["cms.new.seo.rootSlug", "(racine)"],
  seoDefaultSlug: ["cms.new.seo.defaultSlug", "mon-slug"],
  previewMoreFilters: ["cms.new.preview.moreFilters", "Plus de filtres"],
  previewFavorites: ["cms.new.preview.favorites", "Favoris"],
  selectionEdit: ["cms.new.selection.edit", "Modifier la sélection"],
  selectionChoose: [
    "cms.new.selection.choose",
    "Choisir œuvres / multiples / images",
  ],
  changeElement: ["cms.new.picker.changeElement", "Changer l'élément"],
  chooseElement: ["cms.new.picker.chooseElement", "Choisir un élément"],
  mediaNoElementMedia: [
    "cms.new.medias.noElementMedia",
    "Aucun média disponible pour cet élément.",
  ],
  createAbonnementPageFailed: [
    "cms.new.abonnement.createPageFailed",
    "Impossible de créer la page d'abonnement",
  ],
  createMaintenancePageFailed: [
    "cms.new.maintenance.createPageFailed",
    "Impossible de créer la page de maintenance",
  ],
} as const satisfies Record<string, Phrase>;

export function cmsPhrase(
  cmsNew: (key: CmsNewKey, label: string) => string,
  name: keyof typeof CMS_NEW_PHRASES,
): string {
  const [key, label] = CMS_NEW_PHRASES[name];
  return cmsNew(key, label);
}

/** Libellé traduit d'un statut de page CMS avancé (PUBLISHED / ARCHIVED / DRAFT). */
export function puckPageStatusLabel(
  cmsNew: (key: CmsNewKey, label: string) => string,
  status: string | null | undefined,
): string {
  switch (status) {
    case "PUBLISHED":
      return cmsNew("cms.new.pages.published", "Publiée");
    case "ARCHIVED":
      return cmsNew("cms.new.pages.archived", "Archivée");
    case "DRAFT":
      return cmsNew("cms.new.pages.draft", "Brouillon");
    default:
      return status ?? "";
  }
}
